import type { FlowContext, FlowStep, StepRun } from "./types";
import { maxPollDurationMs } from "./engine/poll";

export interface TimeEstimate {
  minMs: number;
  maxMs: number;
  eventSteps: number;
  apiSteps: number;
  skippedSteps: number;
}

export const estimateSteps = (
  steps: FlowStep[],
  ctx?: Pick<FlowContext, "supplier" | "config" | "snapshot" | "partConfirmation" | "child" | "enquiry" | "estimate" | "flags" | "orcaUser" | "invoice" | "shipment">,
  runs?: Record<string, StepRun>
): TimeEstimate => {
  const est: TimeEstimate = { minMs: 0, maxMs: 0, eventSteps: 0, apiSteps: 0, skippedSteps: 0 };
  steps.forEach((step) => {
    const run = runs?.[step.id];
    if (run && (run.status === "completed" || run.status === "skipped")) return;
    const skipReason = ctx && step.shouldSkip ? step.shouldSkip(ctx as FlowContext) : null;
    if (skipReason) {
      est.skippedSteps += 1;
      return;
    }
    const [min, max] = step.estimateMs;
    est.minMs += min;
    if (step.kind === "event") {
      est.eventSteps += 1;
      const pollMax = maxPollDurationMs(ctx?.config.pollMaxAttempts ?? 8);
      est.maxMs += Math.max(max, pollMax);
    } else {
      est.apiSteps += 1;
      est.maxMs += max;
    }
  });
  return est;
};

export const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return "–";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return sec ? `${min}m ${sec}s` : `${min}m`;
};

export const formatEstimateRange = (est: TimeEstimate): string => {
  if (est.minMs === 0 && est.maxMs === 0) return "nothing left to run";
  const approx = est.eventSteps > 0 ? "~" : "";
  return `${approx}${formatDuration(est.minMs)} – ${formatDuration(est.maxMs)}`;
};
